(() => {
  const ar = document.documentElement.lang.startsWith('ar');
  const prefix = (window.Shopify?.routes?.root || (ar ? '/ar/' : '/')).replace(/\/$/, '');
  const pages = new Map();
  let serial = 0;
  let active = 0;
  const queue = [];
  async function limited(task) {
    if (active >= 4) await new Promise(resolve => queue.push(resolve));
    active++;
    try { return await task(); } finally { active--; queue.shift()?.(); }
  }
  async function section(name, page) {
    return limited(async () => {
      const url = new URL(`${prefix}/collections/all`, location.origin);
      url.search = new URLSearchParams({ section_id: name, page: String(page), sort_by: 'best-selling' });
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Catalog response ${response.status}`);
      const html = await response.text();
      if (html.includes('Liquid error')) throw new Error('Catalog rendering failed');
      return new DOMParser().parseFromString(html, 'text/html');
    });
  }
  async function indexPage(page) {
    const doc = await section('khamato-home-catalog-index', page);
    const node = doc.querySelector('[data-home-index]');
    if (!node) throw new Error('Catalog index missing');
    return JSON.parse(node.textContent);
  }
  const catalog = (async () => {
    const first = await indexPage(1);
    const rest = await Promise.all(Array.from({ length: first.pages - 1 }, (_, i) => indexPage(i + 2)));
    return [...first.products, ...rest.flatMap(x => x.products)].filter(p => p.available);
  })();
  function cardsPage(page) {
    if (!pages.has(page)) {
      const pending = section('khamato-home-catalog-cards', page).then(doc => {
        const cards = new Map();
        doc.querySelectorAll('[data-home-card]').forEach(t => cards.set(t.dataset.homeCard, t));
        return cards;
      }).catch(error => { pages.delete(page); throw error; });
      pages.set(page, pending);
    }
    return pages.get(page);
  }
  function matches(product, url) {
    for (const kind of ['need', 'room', 'category']) {
      const key = url.searchParams.get(kind);
      if (key && !key.split(',').some(k => product.tags.includes(`browse-${kind}:${k}`))) return false;
    }
    const brand = url.searchParams.get('brand');
    if (brand && product.brand !== brand) return false;
    const offer = url.searchParams.get('offer');
    if (offer === 'clearance' && !product.tags.includes('offer:clearance')) return false;
    if (offer === 'on-sale' && !product.sale) return false;
    return true;
  }
  function cloneCard(template, root, product) {
    const card = template.content.firstElementChild.cloneNode(true);
    card.dataset.persona = product.persona;
    const scope = root.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '') || root.id;
    const instance = `home-${++serial}`;
    const ids = new Map();
    card.querySelectorAll('[id]').forEach(el => { ids.set(el.id, `${el.id}-${instance}`); el.id = ids.get(el.id); });
    card.querySelectorAll('[for],[aria-controls],[aria-describedby],[aria-labelledby]').forEach(el => {
      for (const name of ['for', 'aria-controls', 'aria-describedby', 'aria-labelledby']) {
        if (el.hasAttribute(name)) el.setAttribute(name, el.getAttribute(name).split(' ').map(id => ids.get(id) || id).join(' '));
      }
    });
    card.querySelectorAll('[data-kp-scope]').forEach(el => el.dataset.kpScope = scope);
    card.querySelectorAll('[data-section-id]').forEach(el => el.dataset.sectionId = scope);
    return card;
  }
  async function init(root, products) {
    if (root.dataset.krReady) return;
    const sectionCopy = {
      'home-offers': ['Offers on Khamato', 'عروض خاماتو', 'Good finds. Better prices.', 'اختيارات تحبها، بأسعار أريح.'],
      'home-needs': ['Khamato Picks', 'اختيارات خاماتو', 'A little help for every home.', 'كل احتياج في بيتك له اختيار مناسب.'],
      'home-rooms': ['Trending by Room', 'اختيارات رائجة لكل غرفة', 'Make room for what you love.', 'خلّي كل ركن شبهك.'],
      'home-best': ['Best Sellers', 'الأكثر مبيعًا', 'Loved in homes. Chosen for yours.', 'اختيارات محبوبة لبيتك.'],
      'home-brands': ['Brand Spotlight', 'ماركات تحت الأضواء', 'Start with a name you know.', 'ابدأ بماركة تعرفها.']
    };
    const header = root.querySelector('.kr__heading');
    const copy = sectionCopy[root.id];
    if (header && copy) {
      header.classList.add('kr-section-header');
      const intro = header.firstElementChild;
      let label = intro.querySelector('p');
      if (!label) { label = document.createElement('p'); intro.prepend(label); }
      label.textContent = copy[ar ? 1 : 0];
      intro.querySelector('h2').textContent = copy[ar ? 3 : 2];
      header.querySelector(':scope>p')?.remove();
      header.after(root.querySelector('.kr__tabs'));
    }

    const tabs = [...root.querySelectorAll('[data-kr-tab]')];
    const states = new Map();
    for (const tab of tabs) {
      const key = tab.dataset.krTab;
      let panel = root.querySelector(`[data-kr-panel="${key}"]`);
      const template = root.querySelector(`[data-kr-template="${key}"]`);
      const source = panel || template?.content;
      const view = source?.querySelector('[data-kr-view]');
      if (!view) { tab.hidden = true; continue; }
      view.textContent = ar ? 'عرض الكل' : 'View all';
      const base = new URL(view.getAttribute('href'), location.origin);
      view.querySelectorAll('svg,[aria-hidden="true"]').forEach(icon => icon.remove());
      const matching = products.filter(p => matches(p, base));
      tab.hidden = matching.length === 0;
      if (!matching.length) continue;
      if (!panel) {
        template.after(template.content.cloneNode(true));
        template.remove();
        panel = root.querySelector(`[data-kr-panel="${key}"]`);
      }
      panel.hidden = true;
      panel.dataset.catalogTotal = matching.length;
      panel.querySelectorAll('[data-kr-move]').forEach(button => button.remove());
      panel.querySelectorAll('.kr__toolbar>div').forEach(group => { if (!group.childElementCount) group.remove(); });
      const poster = panel.querySelector('.kr__banner>div');
      if (poster) {
        let description = poster.querySelector(':scope>span');
        if (!description) { description = document.createElement('span'); poster.querySelector('strong')?.before(description); }
        const offer = base.searchParams.get('offer');
        description.textContent = offer === 'on-sale' ? (ar ? 'اختيارات أكتر لبيتك بسعر أقل.' : 'More for your home. Less on the price.')
          : offer === 'clearance' ? (ar ? 'اكتشف اختيارات التصفية حسب المخزون المتاح.' : 'Discover clearance finds while stock lasts.')
          : base.searchParams.has('room') ? (ar ? 'كمّل تجهيز أوضتك باختيارات تناسب بعض.' : 'Bring your room together, one choice at a time.')
          : base.searchParams.has('category') ? (ar ? 'قارن التشكيلة واختار اللي يناسبك.' : 'Compare the range. Choose what works for you.')
          : base.searchParams.has('brand') ? (ar ? 'اكتشف تشكيلة الماركة واختار المناسب لبيتك.' : 'Explore the brand. Find the right fit for your home.')
          : (ar ? 'اختار اللي يناسب بيتك وأولوياتك.' : 'Find what fits your home and your priorities.');
      }
      const controls = document.createElement('div'); controls.className = 'kr-catalog-controls';
      const personas = panel.querySelector('.kr__personas'); const toolbar = panel.querySelector('.kr__toolbar');
      toolbar.before(controls); if (personas) controls.append(personas); controls.append(toolbar);
      const state = { tab, panel, base, matching, selected: [], loaded: 0, version: 0, busy: false, started: false };
      const progress = document.createElement('div'); progress.className = 'kr-catalog-progress';
      progress.innerHTML = '<span></span>'; progress.setAttribute('role','progressbar');
      progress.setAttribute('aria-label', ar ? 'تقدم تصفح المنتجات' : 'Product browsing progress');
      progress.setAttribute('aria-valuemin','0'); progress.setAttribute('aria-valuemax','100');
      panel.append(progress);
      const rail = panel.querySelector('.kr__rail');
      const updateProgress = () => { const max = rail.scrollWidth - rail.clientWidth; const value = max > 0 ? Math.min(1,Math.abs(rail.scrollLeft)/max) : 1; progress.firstElementChild.style.transform = `scaleX(${Math.max(.05,value)})`; progress.setAttribute('aria-valuenow',String(Math.round(value*100))); };
      rail.addEventListener('scroll',updateProgress,{passive:true}); new ResizeObserver(updateProgress).observe(rail);
      const note = document.createElement('p');
      note.className = 'kr-selection-note visually-hidden'; note.setAttribute('role', 'status'); note.setAttribute('aria-live', 'polite');
      const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'kr-catalog-retry'; retry.hidden = true;
      panel.append(note, retry);
      state.note = note; state.retry = retry; state.updateProgress = updateProgress;
      const personaButtons = [...panel.querySelectorAll('[data-kr-persona]')];
      for (const button of personaButtons) {
        button.hidden = !matching.some(p => p.persona === button.dataset.krPersona);
        button.addEventListener('click', () => choose(state, button.dataset.krPersona));
      }
      state.personaButtons = personaButtons;
      state.persona = personaButtons.find(b => !b.hidden)?.dataset.krPersona || null;
      retry.addEventListener('click', () => loadMore(state));
      panel.querySelectorAll('[data-kr-move]').forEach(button => button.addEventListener('click', async () => {
        const rail = panel.querySelector('.kr__rail');
        if (button.dataset.krMove === '1' && Math.abs(rail.scrollLeft) + rail.clientWidth >= rail.scrollWidth - 20 && state.loaded < state.selected.length) await loadMore(state);
        rail.scrollBy({ left: Number(button.dataset.krMove) * (ar ? -1 : 1) * rail.clientWidth * .85, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }));
      rail.addEventListener('scroll', () => {
        if (state.started && !state.busy && state.loaded < state.selected.length && Math.abs(rail.scrollLeft) > 0 && Math.abs(rail.scrollLeft) + rail.clientWidth >= rail.scrollWidth - rail.clientWidth * .5) loadMore(state);
      }, { passive: true });
      states.set(key, state);
    }
    const availableTabs = tabs.filter(t => !t.hidden);
    if (root.id === 'home-brands') {
      const priority = ['gorenje','grohe','elba','roca','midea','franke','duravit','kludi-rak'];
      const rank = tab => { const i = priority.indexOf(tab.dataset.krTab); return i < 0 ? priority.length : i; };
      availableTabs.sort((a,b) => rank(a)-rank(b));
      const list = root.querySelector('.kr__tabs'); availableTabs.forEach(tab => list.append(tab));
    }

    if (!availableTabs.length) { root.hidden = true; root.dataset.krReady = 'true'; return; }
    async function choose(state, persona) {
      state.persona = persona;
      state.version++; state.busy = false; state.loaded = 0; state.started = true;
      state.selected = state.matching.filter(p => !persona || p.persona === persona);
      state.panel.dataset.catalogMatches = state.selected.length;
      state.panel.querySelector('.kr__rail').replaceChildren();
      state.personaButtons.forEach(b => b.setAttribute('aria-pressed', String(b.dataset.krPersona === persona)));
      const url = new URL(state.base);
      if (persona) url.searchParams.set('persona', persona);
      state.panel.querySelector('[data-kr-view]').href = url.href;
      state.panel.querySelector('.kr__banner').href = url.href;
      return loadMore(state);
    }
    async function loadMore(state) {
      if (state.busy || state.loaded >= state.selected.length) return;
      const version = state.version;
      state.busy = true; state.retry.disabled = true; state.retry.hidden = true; state.note.classList.add('visually-hidden');
      state.note.textContent = ar ? 'جاري تحميل المنتجات…' : 'Loading products…';
      state.panel.setAttribute('aria-busy', 'true');
      const batch = state.selected.slice(state.loaded, state.loaded + 24);
      try {
        const fetched = new Map(await Promise.all([...new Set(batch.map(p => p.page))].map(async page => [page, await cardsPage(page)])));
        if (version !== state.version) return;
        const fragment = document.createDocumentFragment();
        for (const product of batch) {
          const template = fetched.get(product.page).get(String(product.id));
          if (!template) throw new Error('Catalog changed while loading');
          fragment.append(cloneCard(template, root, product));
        }
        state.panel.querySelector('.kr__rail').append(fragment);
        if (root.id === 'home-brands' && !state.panel.querySelector('.kr-brand-montage')) {
          const montage = document.createElement('div'); montage.className = 'kr-brand-montage'; montage.setAttribute('aria-hidden','true');
          state.panel.querySelectorAll('.kr__rail .ks-card__image img').forEach((source,i) => {
            if (i > 2) return;
            const img = source.cloneNode(true); img.alt = ''; img.loading = 'eager'; img.sizes = '(max-width:650px) 28vw, 200px';
            montage.append(img);
          });
          state.panel.querySelector('.kr__banner').append(montage);
        }

        state.loaded += batch.length; requestAnimationFrame(state.updateProgress);
        state.note.textContent = '';
        state.retry.hidden = true;
        
        document.dispatchEvent(new CustomEvent('khamato:payment-plans:refresh'));
      } catch (error) {
        if (version !== state.version) return;
        state.note.classList.remove('visually-hidden');
        state.note.textContent = ar ? 'تعذر تحميل المنتجات. حاول مرة أخرى أو اعرض التشكيلة الكاملة.' : 'Products could not load. Retry or view the full range.';
        state.retry.hidden = false; state.retry.textContent = ar ? 'حاول مرة أخرى' : 'Retry';
        console.error(error);
      } finally {
        if (version === state.version) { state.busy = false; state.retry.disabled = false; state.panel.setAttribute('aria-busy', 'false'); }
      }
    }
    function select(tab, focus = false) {
      const state = states.get(tab.dataset.krTab);
      if (!state) return;
      for (const other of states.values()) other.panel.hidden = other !== state;
      for (const t of availableTabs) { t.setAttribute('aria-selected', String(t === tab)); t.tabIndex = t === tab ? 0 : -1; }
      if (!state.started) choose(state, state.persona); else requestAnimationFrame(state.updateProgress);
      if (focus) tab.focus({ preventScroll: true });
    }
    availableTabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', event => {
        let next;
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = availableTabs.length - 1;
        else if (event.key === 'ArrowRight') next = (i + (ar ? -1 : 1) + availableTabs.length) % availableTabs.length;
        else if (event.key === 'ArrowLeft') next = (i + (ar ? 1 : -1) + availableTabs.length) % availableTabs.length;
        else return;
        event.preventDefault(); select(availableTabs[next], true);
      });
    });
    root.hidden = false;
    root.dataset.krReady = 'true';
    const first = states.get(availableTabs[0].dataset.krTab);
    first.panel.hidden = false;
    availableTabs.forEach((t, i) => { t.setAttribute('aria-selected', String(i === 0)); t.tabIndex = i === 0 ? 0 : -1; });
    const observer = new IntersectionObserver(entries => { if (entries.some(e => e.isIntersecting)) { select(availableTabs[0]); observer.disconnect(); } }, { rootMargin: '300px' });
    observer.observe(root);
  }
  async function start(container = document) {
    try { const products = await catalog; await Promise.all([...container.querySelectorAll('[data-home-restored]')].map(root => init(root, products))); }
    catch (error) {
      console.error(error);
      for (const root of container.querySelectorAll('[data-home-restored]')) {
        root.hidden = false;
        const progress = document.createElement('div'); progress.className = 'kr-catalog-progress';
      progress.innerHTML = '<span></span>'; progress.setAttribute('role','progressbar');
      progress.setAttribute('aria-label', ar ? 'تقدم تصفح المنتجات' : 'Product browsing progress');
      progress.setAttribute('aria-valuemin','0'); progress.setAttribute('aria-valuemax','100');
      panel.append(progress);
      const rail = panel.querySelector('.kr__rail');
      const updateProgress = () => { const max = rail.scrollWidth - rail.clientWidth; const value = max > 0 ? Math.min(1,Math.abs(rail.scrollLeft)/max) : 1; progress.firstElementChild.style.transform = `scaleX(${Math.max(.05,value)})`; progress.setAttribute('aria-valuenow',String(Math.round(value*100))); };
      rail.addEventListener('scroll',updateProgress,{passive:true}); new ResizeObserver(updateProgress).observe(rail);
      const note = document.createElement('p'); note.textContent = ar ? 'تعذر تحميل المنتجات. أعد تحميل الصفحة للمحاولة مرة أخرى.' : 'Products could not load. Reload the page to try again.';
        root.append(note);
      }
    }
  }
  start();
  document.addEventListener('shopify:section:load', event => start(event.target));
})();
